/**
 * The one indicative rate the site quotes, and the maths behind every repayment figure shown.
 *
 * Single source of truth on purpose. The number appears next to a dollar amount AND in the
 * "Based on X%" caption under it on several pages — if those two ever disagree, the site is
 * quoting a repayment it can't justify. Change it here and every page moves together.
 *
 * It is INDICATIVE. Nothing here is a quote or an offer: a real rate depends on credit history,
 * income and a lender's decision, so every surface using this must say so alongside the figure.
 */
export const INDICATIVE_RATE = 0.0895;

/** The rate as it should be written on screen, e.g. "8.95". */
export const INDICATIVE_RATE_PCT = (INDICATIVE_RATE * 100).toFixed(2);

export const DEFAULT_TERM_MONTHS = 60;

export const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    maximumFractionDigits: 0,
  }).format(amount || 0);

/**
 * Weekly repayment on an amortising loan (standard PMT), in whole dollars.
 *
 * Returns 0 for a non-positive principal rather than NaN or Infinity — the sliders can legitimately
 * be dragged to a deposit that covers the whole price, and a repayment panel reading "NaN" on a
 * finance site destroys more trust than it costs to guard against.
 */
/** Weeks in a term, the one place the month→week conversion happens. */
const weeksIn = (termMonths) => Math.round((termMonths / 12) * 52);

/**
 * The exact payment, unrounded. Internal, and the reason `totalInterest` is correct.
 *
 * Rounding to whole dollars is a DISPLAY decision. Multiplying a rounded payment back out over a
 * term is not: at 84 months that is 364 weeks, so up to 50c of rounding becomes an error of well
 * over a hundred dollars in the total. On a page whose entire argument is "here is what the loan
 * really costs", quoting a number that is out by that much would undo the point of writing it.
 */
function exactWeeklyPayment(loanAmount, termMonths, rate) {
  const weeks = weeksIn(termMonths);
  const weeklyRate = rate / 52;
  if (weeklyRate <= 0) return loanAmount / weeks;
  const growth = Math.pow(1 + weeklyRate, weeks);
  return (loanAmount * (weeklyRate * growth)) / (growth - 1);
}

export function weeklyRepayment(loanAmount, termMonths = DEFAULT_TERM_MONTHS, rate = INDICATIVE_RATE) {
  if (!(loanAmount > 0)) return 0;
  return Math.round(exactWeeklyPayment(loanAmount, termMonths, rate));
}

/**
 * Total interest paid over the life of the loan, in whole dollars.
 *
 * Exists so the site can show the thing a repayment figure hides. A longer term always produces a
 * smaller weekly number, which reads like a better deal and is usually the opposite — the same
 * $30,000 costs thousands more over seven years than over three. Every calculator in this market
 * shows the weekly figure; showing what it costs is the honest version, and it is the reason
 * someone would use this one twice.
 *
 * Uses the EXACT payment, not the rounded one shown on screen — see `exactWeeklyPayment`. The two
 * still share one formula, so they cannot drift apart; only the rounding differs, and only where
 * rounding belongs.
 */
export function totalInterest(loanAmount, termMonths = DEFAULT_TERM_MONTHS, rate = INDICATIVE_RATE) {
  if (!(loanAmount > 0)) return 0;
  const total = exactWeeklyPayment(loanAmount, termMonths, rate) * weeksIn(termMonths);
  return Math.max(0, Math.round(total - loanAmount));
}
